// src/Components/Common/MessageActions.js

import React, { useState, useEffect } from 'react';
import { Button, UncontrolledTooltip } from "reactstrap";
import { useSpeechSynthesis } from "react-speech-kit";
import { t } from 'i18next';

// Helper function to detect if a string contains Arabic characters
const containsArabic = (text) => {
  if (!text) return false;
  // This regex checks for characters in the Arabic Unicode block.
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};


const MessageActions = ({ message, onPerfectAnswer, onNotPerfectAnswer }) => {
  const { speak, voices } = useSpeechSynthesis();
  const [isCopied, setIsCopied] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);


  // Reset the "Copied!" text after a few seconds
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  // Stop speech synthesis when the component unmounts
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    };
  }, []);

  // Handler to copy message content to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(message.Message);
    setIsCopied(true);
  };

  // Handler for text-to-speech
  const handleSpeak = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      // Determine the language of the message to select the correct voice
      const isMsgArabic = containsArabic(message.Message);
      const targetLang = isMsgArabic ? 'ar' : 'en';

      // Find a suitable voice, falling back to any available voice if not found
      const voice = voices.find(v => v.lang.startsWith(targetLang)) || voices[0];

      speak({
        text: message.Message,
        voice: voice
      });
      setIsSpeaking(true);
    }
  };


  // --- Helper function to format the response time ---
  const formatResponseTime = (ms) => {
    if (!ms || ms <= 0) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    // Removed mt-2 to work inside the message bubble
 
    <div className="message-actions d-flex align-items-center gap-2">
      {/* --- Copy Button --- */}
      <button
        id={`copy-btn-${message.ID}`}
        className="btn btn-ghost-secondary btn-icon"
        onClick={handleCopy}
      >
        <i className={isCopied ? "ri-check-line" : "ri-file-copy-line"}></i>
      </button>
      <UncontrolledTooltip placement="top" target={`copy-btn-${message.ID}`}>
        {isCopied ? t("Copied") : t("Copy")}
      </UncontrolledTooltip>

      {/* --- Text-to-Speech Button --- */}
      { message.Link ? 
        <button
          id={`view-btn-${message.ID}`}
          className="btn btn-ghost-secondary btn-icon"
          onClick={() => window.open(message.Link, "_blank")}
        >
          <i className="ri-eye-line"></i>
        </button> : null
      }

      {/* --- Text-to-Speech Button --- */}
       <button
        id={`speak-btn-${message.ID}`}
        className="btn btn-ghost-secondary btn-icon"
        onClick={handleSpeak}
      >
        <i className={isSpeaking ? "ri-volume-mute-line" : "ri-volume-up-line"}></i>
      </button>
      <UncontrolledTooltip placement="top" target={`speak-btn-${message.ID}`}>
        {isSpeaking ? t("Stop") : t("Listen")}
      </UncontrolledTooltip>


      {/* --- Conditional Feedback Buttons --- */}
      { message.IsLiked ?
        <div className="d-flex gap-2 ms-auto">
            <Button
              size="sm"
              color={message.IsLiked === 1 ? "success" : "light"}
              onClick={() => onPerfectAnswer(message)}
            >
              <i className="ri-thumb-up-line align-bottom"></i> {t("Perfect")}
            </Button>
            <Button
              size="sm"
              color={message.IsLiked === 2 ? "danger" : "light"}
              onClick={() => onNotPerfectAnswer(message)}
            >
              <i className="ri-thumb-down-line align-bottom"></i> {t("Not Perfect")}
            </Button>
        </div> : null
      }
      
      {/* --- NEW: Display Response Time if it exists --- */}
      {message.ResponseTimeMs ? (
        <small id={`time-info-${message.ID}`} className="text-muted" style={{ cursor: 'default', fontSize: '12px' }}>
          {formatResponseTime(message.ResponseTimeMs)}
        </small>
      ) : null}

      {/* {message.SenderName && (
        <small className="text-muted">{message.SenderName}</small>
      )} */}

      

    </div>
  );
};

export default React.memo(MessageActions);